import { Autocomplete, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import {
  ConductConnotation,
  ConductType,
  ConductTypeRepresentation,
  FilterKeys,
} from '@schooly/api';
import { DateSelect } from '@schooly/components/filters';
import {
  ArrowRightIcon,
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  Loading,
  ModalSmall,
  Spin,
} from '@schooly/style';
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { AddStudentsExternalSidebar } from '../../../components/common/AddStudentsExternalSidebar/AddStudentsExternalSidebar';
import PersonCardSelectable from '../../../components/common/PersonCard/PersonCardSelectable';
import { Counter } from '../../../components/uikit/Counter/Counter.styled';
import { FormTextField } from '../../../components/uikit-components/FormTextField/FormTextField';
import {
  ModalContent,
  ModalFooter,
  ModalLarge,
  ModalMain,
  ModalPanel,
} from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalAccessDenied } from '../../../components/uikit-components/Modal/ModalAccessDenied';
import { ModalEmptyMainArea } from '../../../components/uikit-components/Modal/ModalEmptyMainArea';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { ModalPeopleExtensionPanel } from '../../../components/uikit-components/Modal/ModalPeopleExtensionPanel';
import { useFormError } from '../../../hooks/useFormError';
import useUserCounts from '../../../hooks/useUserCounts';
import { WithRef } from '../../../hooks/useWithRef';
import { formatDateDefault } from '../../../utils/formatDate';
import { ConductConnotationIndicator } from '../ConductGrid';
import { ConductCreateModalPreview } from './ConductCreateModalPreview';
import { ConductManageGrid } from './ConductManageGrid';
import { ConductTypeOption } from './ConductTypeOption';
import {
  ConductCreateModalContextState,
  ConductCreateModalMode,
  useConductCreateModal,
} from './useConductCreateModal';

// Cannot use FilterKeys.Group cuz it breaks existing filters
export const CREATE_CONDUCT_GROUP_SEARCH_PARAM = 'for_group';

const shouldResetEntryValues = (prevType: ConductType, newType: ConductType): boolean => {
  return (
    prevType.type !== newType.type ||
    prevType.type === ConductTypeRepresentation.SELECT ||
    newType.type === ConductTypeRepresentation.SELECT
  );
};

const ConductCreateModal: FC = () => {
  const { $t } = useIntl();
  const navigate = useNavigate();
  const { userCounts, fetching: userCountsFetching } = useUserCounts();
  const {
    actions,
    form,
    errors,
    types,
    conductId,
    externalStudentRelationId,
    singleStudent,
    hasManagerPermission,
    ...contextState
  } = useConductCreateModal();

  const resetEntryValues = useCallback(
    (entries: ConductCreateModalContextState['conductEntryPatches']) => {
      const updated = Object.fromEntries(
        Object.entries(entries).map(([key, entry]) => [
          key,
          entry.title || entry.value ? { ...entry, title: null, value: null } : entry,
        ]),
      );

      actions.setConductEntryPatches(updated);
    },
    [actions],
  );

  useEffect(() => {
    if (!userCountsFetching && !userCounts?.student) {
      navigate('/conduct');
    }
  }, [navigate, userCounts?.student, userCountsFetching]);

  const containerRef = useRef<HTMLDivElement>(null);
  const conductTypes = [...(types ?? [])]
    .sort((a, b) => a.order - b.order)
    .sort((a, b) => a.connotation - b.connotation);

  const { renderError } = useFormError();
  const isActive =
    contextState.mode === ConductCreateModalMode.Students ||
    contextState.mode === ConductCreateModalMode.Table;

  const singleStudentContent = useMemo(
    () => (
      <ModalPanel
        ref={containerRef}
        active={contextState.mode === ConductCreateModalMode.Table}
        fullHeight
        flat
      >
        <Stack px={2.5} pt={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={0.25} alignItems="center">
              <Typography variant="h2">
                <FormattedMessage
                  id={
                    contextState.mode === ConductCreateModalMode.Table
                      ? 'section-Students'
                      : 'userType-student'
                  }
                />
              </Typography>
              <Counter>{contextState.selectedStudents?.length}</Counter>
            </Stack>
          </Stack>

          {contextState.mode === ConductCreateModalMode.Initial && (
            <Stack pt={2.5} pl={0.5}>
              {contextState.selectedStudents?.map((student) => (
                <PersonCardSelectable
                  key={student.relation_id}
                  user={student}
                  userType="student"
                  isListItem
                  isSingularSelected={false}
                />
              ))}
            </Stack>
          )}
        </Stack>
        {contextState.mode === ConductCreateModalMode.Table && (
          <Stack pt={2.5}>
            <ConductManageGrid
              errors={contextState.gridErrors}
              selectedStudents={contextState.selectedStudents}
              conductEntryPatches={contextState.conductEntryPatches}
              selectedConductType={contextState.selectedConductType}
              selectConductTypes={conductTypes}
              setConductEntryPatches={actions.setConductEntryPatches}
            />
          </Stack>
        )}
      </ModalPanel>
    ),
    [
      actions.setConductEntryPatches,
      contextState?.conductEntryPatches,
      contextState?.gridErrors,
      contextState?.mode,
      contextState?.selectedConductType,
      contextState?.selectedStudents,
      conductTypes,
    ],
  );

  const isDateFieldFocused = contextState.focusedField === 'date';

  const handleOpenDatePicker = useCallback(() => {
    if (isDateFieldFocused) {
      actions.clearFocusField();
    }
  }, [actions, isDateFieldFocused]);

  if (!hasManagerPermission) {
    return (
      <ModalSmall open onClose={actions.closeModal}>
        <ModalHeader active>
          <IconButton onClick={actions.closeModal}>
            <CrossIcon />
          </IconButton>
        </ModalHeader>
        <ModalContent active>
          <ModalAccessDenied />
        </ModalContent>
      </ModalSmall>
    );
  }

  const ModalComponent =
    contextState.mode === ConductCreateModalMode.Table ? ModalLarge : ModalSmall;

  const renderContent = () => {
    if (contextState.loading) {
      return <Loading />;
    }

    const isDateLoading = contextState.dateLoading;
    const initialDate = formatDateDefault(contextState.date);

    return (
      <form onSubmit={form.onSubmit(actions.submitForm)}>
        <ModalHeader
          active={contextState.mode !== ConductCreateModalMode.Students}
          title={$t({ id: 'conduct-Title' })}
        >
          <IconButton onClick={actions.closeModal}>
            <CrossIcon />
          </IconButton>
        </ModalHeader>
        <ModalPanel withBorderBottom active={contextState.mode !== ConductCreateModalMode.Students}>
          {contextState.mode === ConductCreateModalMode.Initial && (
            <Stack gap={2.5} data-test-id="conduct-modal-new-log">
              <Autocomplete
                openOnFocus
                fullWidth
                disableClearable
                value={contextState.selectedConductType}
                options={conductTypes ?? []}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                groupBy={(option) =>
                  option.connotation === ConductConnotation.POSITIVE
                    ? $t({ id: 'conduct-connotation-POSITIVE' }).toUpperCase()
                    : $t({ id: 'conduct-connotation-NEGATIVE' }).toUpperCase()
                }
                onChange={(e, type) => {
                  const { selectedConductType: prevType, conductEntryPatches } = contextState;
                  if (prevType && shouldResetEntryValues(prevType, type)) {
                    resetEntryValues(conductEntryPatches);
                  }

                  form.set('selectedConductType', type);
                }}
                renderInput={(params) => (
                  <FormTextField
                    {...params}
                    label={$t({ id: 'conduct-ConductType' })}
                    autoFocus={contextState.focusedField === 'selectedConductType'}
                    fullWidth
                    dropdown
                    required
                    testId="conduct-modal-type-select"
                    showEndAdornment={!contextState.selectedConductType}
                    startAdornment={
                      contextState.selectedConductType && (
                        <Box sx={{ ml: '13px', mr: 1 }}>
                          <ConductConnotationIndicator
                            connotation={contextState.selectedConductType?.connotation}
                          />
                        </Box>
                      )
                    }
                    error={!!errors?.selectedConductType}
                    helperText={renderError(errors?.selectedConductType)}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <ConductTypeOption {...option} />
                  </li>
                )}
              />

              <DateSelect
                date={contextState.date}
                onSetDate={actions.onDateChange}
                onOpen={handleOpenDatePicker}
                requiredLabel="required"
                placeholder={$t({ id: 'conduct-Date-modal' })}
                isLoading={isDateLoading}
                disabled={isDateLoading}
                opened={isDateFieldFocused || undefined}
                renderRightIcon={isDateLoading ? () => <Spin /> : undefined}
                error={
                  errors?.date
                    ? {
                        type: 'validate',
                        message: renderError(errors.date) || '',
                      }
                    : undefined
                }
                testId="conduct-log-date-select"
              />
            </Stack>
          )}

          {isActive && (
            <ConductCreateModalPreview
              {...contextState}
              onClick={(focusedField: keyof ConductCreateModalContextState) => {
                actions.setContextState({
                  mode: ConductCreateModalMode.Initial,
                  focusedField,
                });
              }}
            />
          )}
        </ModalPanel>

        {singleStudent ? (
          singleStudentContent
        ) : (
          <ModalPeopleExtensionPanel
            titleId="section-Students"
            addActionId="groups-AddStudents"
            editActionId="groups-EditStudents"
            count={contextState.selectedStudents?.length}
            active={isActive}
            headerMode={contextState.mode === ConductCreateModalMode.Table}
            disabled={!contextState.selectedConductType || !contextState.date}
            errorId={(errors?.selectedStudents as MessageDescriptor)?.id}
            onAddClick={actions.setStateHandler('mode', ConductCreateModalMode.Students)}
            sidebarContent={
              <AddStudentsExternalSidebar
                students={contextState.selectedStudents}
                onStudentClick={actions.onStudentClick}
                initialFilters={
                  initialDate
                    ? {
                        [FilterKeys.Date]: [initialDate],
                      }
                    : undefined
                }
              />
            }
            showActionIcon={
              !contextState.isEditing && contextState.mode === ConductCreateModalMode.Table
            }
            withoutGap
          >
            <ModalContent
              ref={containerRef}
              withBorderBottom
              active
              flat
              sx={{ pt: 0 }}
              data-test-id="conduct-modal-update-log"
            >
              {!contextState.selectedStudents?.length ? (
                <ModalEmptyMainArea label={$t({ id: 'section-Students' }).toLowerCase()} />
              ) : (
                <>
                  {contextState.mode === ConductCreateModalMode.Students && (
                    <Stack sx={{ overflowY: 'auto', px: 2.5 }}>
                      {contextState.selectedStudents?.map((student) => (
                        <PersonCardSelectable
                          key={student.relation_id}
                          isStaticSelected
                          user={student}
                          userType="student"
                          onClick={() => actions.onStudentClick(student)}
                          isListItem
                          isDisabled={externalStudentRelationId === student.relation_id}
                        />
                      ))}
                    </Stack>
                  )}

                  {contextState.mode === ConductCreateModalMode.Table && (
                    <ConductManageGrid
                      errors={contextState.gridErrors}
                      selectedStudents={contextState.selectedStudents}
                      conductEntryPatches={contextState.conductEntryPatches}
                      selectedConductType={contextState.selectedConductType}
                      selectConductTypes={conductTypes}
                      setConductEntryPatches={actions.setConductEntryPatches}
                    />
                  )}
                </>
              )}
            </ModalContent>
          </ModalPeopleExtensionPanel>
        )}
        <ModalMain />

        <ModalFooter
          sx={{ justifyContent: conductId ? 'space-between' : 'flex-end' }}
          active={contextState.mode === ConductCreateModalMode.Table}
        >
          {conductId && (
            <Button
              variant="outlined"
              startIcon={contextState.deleting ? <Spin /> : <DeleteIcon />}
              disabled={contextState.deleting || contextState.saving}
              onClick={actions.deleteConduct}
              data-test-id="modal-delete"
            >
              <FormattedMessage id="action-Delete" />
            </Button>
          )}

          {contextState.mode !== ConductCreateModalMode.Table && (
            <Button type="submit" endIcon={<ArrowRightIcon />} data-test-id="modal-continue">
              <FormattedMessage id="action-Continue" />
            </Button>
          )}

          {contextState.mode === ConductCreateModalMode.Table && (
            <Button
              endIcon={contextState.saving ? <Spin /> : <CheckIcon />}
              disabled={contextState.saving || contextState.deleting}
              onClick={actions.saveConduct}
              data-test-id="modal-submit"
            >
              <FormattedMessage id="action-Save" />
            </Button>
          )}
        </ModalFooter>
      </form>
    );
  };

  return (
    <ModalComponent open onClose={actions.closeModal}>
      <WithRef containerRef={containerRef}>{renderContent()}</WithRef>
    </ModalComponent>
  );
};

export default ConductCreateModal;
